/* eslint-disable react/jsx-props-no-spreading */
import type { FilterOptionOption } from 'react-select/dist/declarations/src/filters';
import { SharedComponent } from '../../../enums';
import ClearButton from '../ClearButton/ClearButton';
import type { FormFieldVariant } from '../enums';
import FormField from '../FormField/FormField';
import type { FormFieldProps, FormFieldType, PossibleValue } from '../types';
import { FieldType } from '../types';
import type { ReactElement } from 'react';
import React, { useEffect, useRef } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import type { FieldValues } from 'react-hook-form';
import type { FieldPath } from 'react-hook-form/dist/types/path';
import type {
  MultiValueProps,
  ValueContainerProps,
  Props,
  MenuProps,
  ControlProps,
  ClearIndicatorProps,
  DropdownIndicatorProps,
  PlaceholderProps,
  InputProps
} from 'react-select';
import Select, { components } from 'react-select';
import { useUIDSeed } from 'react-uid';
import useTranslation from '../../useTranslation';
import localStyles from './SearchableMultiSelectField.module.pcss';
import { useReactSelectTheme } from '../../../helpers/styles/ReactSelectThemeHelper';
import type SelectType from 'react-select/dist/declarations/src/Select';

/**
 * Search Select field's specification. NameT is the name of the field and DataT is
 * the definition of bean that holds all the fields for the form.
 */
export interface SearchableMultiSelectFieldSpec<
  NameT extends FieldPath<DataT>,
  DataT extends FieldValues
> extends FormFieldType<NameT, DataT, FormFieldVariant.SEARCHABLE_MULTI_SELECT> {
  /**
   * Array of options that populate the select menu
   */
  loadOptions: ReadonlyArray<unknown>;

  /**
   * Placeholder text when inputValue is empty
   */
  inputPlaceholder?: string;

  /**
   * Custom multi value component to use.
   */
  customMultiValue?: React.FC<React.PropsWithChildren<unknown>>;
}

/**
 * Classname prop type
 */
type ClassNameProp = {
  className: string;
};

/**
 * Props passed to the custom Components used in react-select
 */
interface CustomSelectProps {
  /**
   * react-select ValueContainer custom props
   */
  valueContainerProps: ClassNameProp;
  /**
   * react-select Control custom props
   */
  controlProps: ClassNameProp & { focusedClassName: string };
  /**
   * react-select MultiValue custom props
   */
  multiValueProps: ClassNameProp & {
    itemComponent: React.FC<
      React.PropsWithChildren<MultiValueProps<Record<string, string>, boolean>>
    >;
  };
  /**
   * react-select Placeholder custom props
   */
  placeHolderProps: ClassNameProp;
  /**
   * react-select ClearIndicator custom props
   */
  clearIndicatorProps: ClassNameProp;
  /**
   * react-select Menu custom props
   */
  menuProps: ClassNameProp;
  /**
   * react-select SeparatorIndicator custom props
   */
  dropdownIndicatorProps: ClassNameProp;
  /**
   * the ref attribute to be passed to the select component
   */
  selectRef: React.MutableRefObject<SelectType<Record<string, string>, boolean>>;
}

/**
 * Override of the react-select MultiValue component that is used to add a
 * custom class name to the element and provide a custom "chip" when an item
 * is added to the input.
 */
const MultiValue: React.FC<
  React.PropsWithChildren<MultiValueProps<Record<string, string>, boolean>>
> = props => {
  const { children, selectProps } = props;
  const { multiValueProps } = selectProps as Props<Record<string, string>, boolean> &
    CustomSelectProps;
  const { className, itemComponent: CustomMultiValue } = multiValueProps;

  return (
    <components.MultiValue className={className} {...props}>
      {CustomMultiValue ? <CustomMultiValue {...props}>{children}</CustomMultiValue> : children}
    </components.MultiValue>
  );
};

/**
 * Override of the react-select Menu component that is used to add a
 * custom class name to the element.
 */
const Menu: React.FC<
  React.PropsWithChildren<MenuProps<Record<string, string>, boolean>>
> = props => {
  const { children, selectProps } = props;
  const { menuProps } = selectProps as Props<Record<string, string>, boolean> & CustomSelectProps;
  const { className } = menuProps;

  return (
    <components.Menu {...props} className={className}>
      {children}
    </components.Menu>
  );
};

/**
 * Override of the react-select Control component that is used to add
 * custom class names to the element.
 */
const Control: React.FC<
  React.PropsWithChildren<ControlProps<Record<string, string>, boolean>>
> = props => {
  const { children, selectProps, isFocused } = props;
  const { controlProps } = selectProps as Props<Record<string, string>, boolean> &
    CustomSelectProps;

  const { className, focusedClassName } = controlProps;

  return (
    <components.Control className={`${className} ${isFocused ? focusedClassName : ''}`} {...props}>
      {children}
    </components.Control>
  );
};

/**
 * Override of the react-select ClearIndicator component that is used to add a
 * custom class name to the element.
 */
const ClearIndicator: React.FC<
  React.PropsWithChildren<ClearIndicatorProps<Record<string, string>, boolean>>
> = props => {
  const { clearValue, selectProps } = props;
  const { clearIndicatorProps } = selectProps as Props<Record<string, string>, boolean> &
    CustomSelectProps;

  const { className } = clearIndicatorProps;

  return (
    <ClearButton
      onClear={() => {
        clearValue();
      }}
      className={className}
    />
  );
};

/**
 * Override of the react-select Placeholder component that is used to add a
 * custom class name to the element.
 */
const Placeholder: React.FC<
  React.PropsWithChildren<PlaceholderProps<Record<string, string>, boolean>>
> = props => {
  const { children, selectProps } = props;
  const { placeHolderProps, value } = selectProps as Props<Record<string, string>, boolean> &
    CustomSelectProps;
  const { className } = placeHolderProps;

  if (value && Object.keys(value).length > 0) {
    return null;
  }

  return (
    <components.Placeholder className={className} {...props}>
      {children}
    </components.Placeholder>
  );
};

/**
 * Override of the react-select DropdownIndicator component that is used to add a
 * custom class name to the element.
 */
const DropdownIndicator: React.FC<
  React.PropsWithChildren<DropdownIndicatorProps<Record<string, string>, boolean>>
> = props => {
  const { selectProps } = props;
  const { dropdownIndicatorProps } = selectProps as Props<Record<string, string>, boolean> &
    CustomSelectProps;

  const { className } = dropdownIndicatorProps;

  return <components.DropdownIndicator {...props} className={className} />;
};

/**
 * Override of the react-select Placeholder component that is used to add a
 * custom class name to the element.
 */
const Input: React.FC<
  React.PropsWithChildren<InputProps<Record<string, string>, boolean>>
> = props => {
  const { children } = props;

  return <components.Input {...props}>{children}</components.Input>;
};

/**
 * Override of the react-select ValueContainer component that is used to add a
 * custom class name to the element.
 */
const ValueContainer: React.FC<
  React.PropsWithChildren<ValueContainerProps<Record<string, string>, boolean>>
> = props => {
  const { children, selectProps } = props;
  const { valueContainerProps } = selectProps as Props<Record<string, string>, boolean> &
    CustomSelectProps;
  const { className } = valueContainerProps;

  const childrenArray = React.Children.toArray(children);
  const containsPlaceholder = childrenArray.some(
    (child: ReactElement) => child.type === Placeholder
  );
  if (childrenArray.length > 1) {
    const lastChild = childrenArray.pop();
    childrenArray.unshift(lastChild);
  }

  return (
    <components.ValueContainer
      {...props}
      className={`${className} ${!containsPlaceholder && 'no-placeholder'}`}
    >
      {childrenArray}
    </components.ValueContainer>
  );
};

/**
 * Renders a search-multi-select field using react-select which can be backed by
 * an array of options
 *
 * @author Lucas Fernandez
 */
const SearchableMultiSelectField = <NameT extends FieldPath<DataT>, DataT extends FieldValues>({
  fieldSpec,
  formId,
  i18n,
  className,
  formMethods,
  onFormSubmit,
  submitOnChange,
  allFields
}: FormFieldProps<
  NameT,
  DataT,
  SearchableMultiSelectFieldSpec<NameT, DataT>
>): React.ReactElement => {
  const {
    loadOptions,
    defaultValue,
    inputPlaceholder,
    focus,
    customMultiValue: CustomMultiValue
  } = fieldSpec;
  const cx = useClassNameMapper(localStyles);
  const selectRef = useRef<SelectType<Record<string, string>, boolean>>(null);
  const hasBeenFocused = useRef<boolean>(false);
  const uidSeed = useUIDSeed();
  const inputId = uidSeed('search-multi-select-input-id');
  const { formatMessage: localFormatMessage, loading: localTextLoading } = useTranslation(
    SharedComponent.SEARCHABLE_MULTI_SELECT_FIELD
  );

  const placeholder = inputPlaceholder ?? localFormatMessage('placeholder');

  const reactSelectTheme = useReactSelectTheme();

  useEffect(() => {
    /**
     * Set focus on the input element if focus is specified on the form spec
     */
    if (!!selectRef.current && focus && !hasBeenFocused.current) {
      selectRef.current.focus();
      hasBeenFocused.current = true;
    }
  }, [focus]);

  if (localTextLoading) {
    return null;
  }

  /**
   * Search logic for filtering the searchableMultiSelectFieldEntries.
   * Returns true for entry starting with the given input string.
   *
   * @param entry record with the entry
   * @param input inputValue string
   */
  function filterOptions(entry: FilterOptionOption<string>, input: string) {
    return entry.label.toLowerCase().startsWith(input.toLowerCase());
  }

  const customSelectProps: CustomSelectProps = {
    valueContainerProps: {
      className: cx('lia-select-value-container')
    },
    controlProps: {
      className: cx('lia-control'),
      focusedClassName: cx('lia-control-is-focused')
    },
    multiValueProps: {
      className: cx('lia-multi-value'),
      itemComponent: CustomMultiValue
    },
    clearIndicatorProps: {
      className: cx('lia-clear-indicator')
    },
    menuProps: {
      className: cx('lia-menu')
    },
    placeHolderProps: {
      className: cx('lia-placeholder')
    },
    dropdownIndicatorProps: {
      className: cx('lia-dropdown-indicator')
    },
    selectRef: selectRef
  };

  function handleOptions(input: { inputValue: string }) {
    if (input.inputValue === '') {
      return null;
    } else {
      return localFormatMessage('noResultsText');
    }
  }

  function getOption(value: string | PossibleValue<string, unknown>) {
    if (typeof value === 'string') {
      return loadOptions.find(option => value == (option as PossibleValue<string, unknown>).value);
    }
    return value;
  }

  return (
    <FormField
      fieldSpec={fieldSpec}
      fieldType={FieldType.CONTROLLED}
      formId={formId}
      i18n={i18n}
      formMethods={formMethods}
      onFormSubmit={onFormSubmit}
      submitOnChange={submitOnChange}
      allFormFields={allFields}
      className={cx(className)}
    >
      {({
        render: {
          field: { value, onChange }
        }
      }) => {
        return (
          <Select
            inputId={inputId}
            ref={selectRef}
            tabSelectsValue={false}
            menuPlacement={'auto'}
            maxMenuHeight={-1}
            placeholder={placeholder}
            isMulti={true}
            isClearable={true}
            blurInputOnSelect={false}
            onChange={(event, action) => {
              if (action.action === 'remove-value') {
                selectRef.current.focus();
              }
              onChange(event);
            }}
            options={loadOptions}
            value={value.map((value_: string) => getOption(value_))}
            defaultValue={defaultValue}
            filterOption={filterOptions}
            hideSelectedOptions={true}
            openMenuOnFocus={true}
            closeMenuOnSelect={false}
            noOptionsMessage={handleOptions}
            components={{
              Input,
              ValueContainer,
              Control,
              Menu,
              ClearIndicator,
              DropdownIndicator,
              Placeholder,
              MultiValue
            }}
            theme={reactSelectTheme}
            {...customSelectProps}
          />
        );
      }}
    </FormField>
  );
};

export default SearchableMultiSelectField;
